import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Descriptions, Button, Result, Timeline, Typography, Space, Modal, Form, Select, Input, App } from 'antd';
import { CustomerServiceOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../Components/common/PageHeader';
import { LoadingState, ApiErrorState } from '../../Components/common/StateViews';
import StatusTag from '../../Components/common/StatusTag';
import useApiData from '../../Hooks/useApiData';
import ticketService from '../../Services/ticketService';
import { TicketStatusLabels, TicketStatusColors, optionsFromLabels, labelOf } from '../../Constants/enums';
import { formatDateTime } from '../../Utils/format';
import { toApiError } from '../../api/axiosClient';

const TIMELINE_COLOR = { 0: 'red', 1: 'gold', 2: 'green' };

const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { data, loading, error, reload } = useApiData(() => ticketService.getTicket(id), [id]);
  const [assignees, setAssignees] = useState([]);
  const [statusOpen, setStatusOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusForm] = Form.useForm();
  const [assignForm] = Form.useForm();

  useEffect(() => {
    ticketService.getAssignees().then(setAssignees).catch(() => setAssignees([]));
  }, []);

  const assigneeName = (uid) => {
    const a = assignees.find((x) => x.id === uid);
    return a ? `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email : uid ? `User #${uid}` : 'Unassigned';
  };
  const assigneeOptions = assignees.map((a) => ({ value: a.id, label: `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email }));

  const submitStatus = async () => {
    let v;
    try {
      v = await statusForm.validateFields();
    } catch {
      return; // invalid — field errors render inline
    }
    setSaving(true);
    try {
      await ticketService.setStatus(id, v.status, v.note);
      message.success('Ticket status updated.');
      setStatusOpen(false);
      statusForm.resetFields();
      reload();
    } catch (err) {
      message.error(toApiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  const submitAssign = async () => {
    let v;
    try {
      v = await assignForm.validateFields();
    } catch {
      return; // invalid — field errors render inline
    }
    setSaving(true);
    try {
      await ticketService.assign(id, v.assignedToUserId, v.note);
      message.success('Ticket reassigned.');
      setAssignOpen(false);
      assignForm.resetFields();
      reload();
    } catch (err) {
      message.error(toApiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  const back = () => navigate('/cio/support');

  if (error?.status === 404) {
    return (
      <div>
        <PageHeader title="Ticket" onBack={back} />
        <Result icon={<CustomerServiceOutlined style={{ color: '#599c16' }} />} title="Ticket not found" />
      </div>
    );
  }
  if (loading) {
    return (<div><PageHeader title="Ticket" onBack={back} /><LoadingState /></div>);
  }
  if (error) {
    return (<div><PageHeader title="Ticket" onBack={back} /><ApiErrorState error={error} onRetry={reload} /></div>);
  }
  if (!data) return null;

  const history = data.history || [];

  const describe = (h) => {
    const parts = [];
    if (h.previousStatus !== h.currentStatus && h.currentStatus !== null && h.currentStatus !== undefined) {
      parts.push(
        h.previousStatus === null || h.previousStatus === undefined
          ? `Created as ${labelOf(TicketStatusLabels, h.currentStatus)}`
          : `${labelOf(TicketStatusLabels, h.previousStatus)} → ${labelOf(TicketStatusLabels, h.currentStatus)}`
      );
    }
    if (h.assignedToUserId) parts.push(`Assigned to ${assigneeName(h.assignedToUserId)}`);
    return parts.join(' · ') || 'Update';
  };

  return (
    <div>
      <PageHeader
        title={
          <Space>
            {data.ticketNumber}
            <StatusTag value={data.status} labels={TicketStatusLabels} colors={TicketStatusColors} />
          </Space>
        }
        subtitle={`${data.clientName || ''} · ${data.accountNumber || ''}`}
        docTitle={data.ticketNumber || 'Ticket'}
        onBack={back}
        extra={
          <Space wrap>
            <Button onClick={() => { assignForm.setFieldsValue({ assignedToUserId: data.assignedToUserId ?? undefined }); setAssignOpen(true); }}>Reassign</Button>
            <Button type="primary" onClick={() => { statusForm.setFieldsValue({ status: data.status }); setStatusOpen(true); }}>Change Status</Button>
          </Space>
        }
      />
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Ticket" size="small">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Client">{data.clientName || '-'}</Descriptions.Item>
              <Descriptions.Item label="Account">{data.accountNumber || '-'}</Descriptions.Item>
              <Descriptions.Item label="Issue">{data.issue || '-'}</Descriptions.Item>
              <Descriptions.Item label="Time occurred">{formatDateTime(data.occurredAtUtc)}</Descriptions.Item>
              <Descriptions.Item label="Assigned to">{assigneeName(data.assignedToUserId)}</Descriptions.Item>
              <Descriptions.Item label="Resolved at">{formatDateTime(data.resolvedAtUtc)}</Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="History" size="small">
            {history.length === 0 ? (
              <Typography.Text type="secondary">No history entries yet.</Typography.Text>
            ) : (
              <Timeline
                items={[...history]
                  .sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn))
                  .map((h) => ({
                    color: TIMELINE_COLOR[h.currentStatus] || 'gray',
                    children: (
                      <div>
                        <div style={{ fontWeight: 600 }}>{describe(h)}</div>
                        {h.note ? <div>{h.note}</div> : null}
                        <div style={{ color: '#667085', fontSize: 12 }}>
                          {formatDateTime(h.createdOn)}
                          {h.changeSource ? ` · ${h.changeSource}` : ''}
                        </div>
                      </div>
                    ),
                  }))}
              />
            )}
          </Card>
        </Col>
      </Row>

      <Modal title="Change Status" open={statusOpen} onOk={submitStatus} onCancel={() => setStatusOpen(false)} confirmLoading={saving} okText="Update" destroyOnHidden>
        <Form form={statusForm} layout="vertical" requiredMark="optional">
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select options={optionsFromLabels(TicketStatusLabels)} />
          </Form.Item>
          <Form.Item name="note" label="Note (optional)">
            <Input.TextArea rows={3} placeholder="What changed / what was done" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Reassign Ticket" open={assignOpen} onOk={submitAssign} onCancel={() => setAssignOpen(false)} confirmLoading={saving} okText="Reassign" destroyOnHidden>
        <Form form={assignForm} layout="vertical" requiredMark="optional">
          <Form.Item name="assignedToUserId" label="Assign to" rules={[{ required: true }]}>
            <Select options={assigneeOptions} placeholder="Select a support person" />
          </Form.Item>
          <Form.Item name="note" label="Note (optional)">
            <Input.TextArea rows={3} placeholder="Handover note" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TicketDetail;
